/**
 * Upcoming Deadlines Page
 * Timeline view of all disclosure deadlines
 */
import React, { useState } from 'react';
import { 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileText,
  Shield,
  Leaf
} from 'lucide-react';

const DEADLINES = [
  {
    id: 1,
    title: 'TCFD Climate Report Submission',
    cycle: 'FY2024 TCFD Report',
    framework: 'TCFD',
    date: '2025-04-15',
    daysRemaining: 12,
    status: 'urgent',
    type: 'submission',
  },
  {
    id: 2,
    title: 'CSRD Evidence Collection Complete',
    cycle: 'FY2024 CSRD/ESRS',
    framework: 'CSRD',
    date: '2025-05-01',
    daysRemaining: 28,
    status: 'upcoming',
    type: 'milestone',
  },
  {
    id: 3,
    title: 'CDP Climate Questionnaire Response',
    cycle: '2025 CDP Climate',
    framework: 'CDP',
    date: '2025-07-31',
    daysRemaining: 76,
    status: 'planned',
    type: 'submission',
  },
  {
    id: 4,
    title: 'CSRD Final Submission',
    cycle: 'FY2024 CSRD/ESRS',
    framework: 'CSRD',
    date: '2025-06-30',
    daysRemaining: 45,
    status: 'upcoming',
    type: 'submission',
  },
  {
    id: 5,
    title: 'CSDDD Due Diligence Assessment',
    cycle: 'Q1 2026 CSDDD Due Diligence',
    framework: 'CSDDD',
    date: '2025-12-31',
    daysRemaining: 229,
    status: 'planned',
    type: 'milestone',
  },
];

const STATUS_CONFIG = {
  urgent: { label: 'Urgent', color: 'red', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  upcoming: { label: 'Upcoming', color: 'amber', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  planned: { label: 'Planned', color: 'blue', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  completed: { label: 'Completed', color: 'emerald', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

const FRAMEWORK_ICONS = {
  TCFD: Clock,
  CSRD: FileText,
  CDP: Leaf,
  CSDDD: Shield,
};

const DeadlineCard = ({ deadline }) => {
  const statusConfig = STATUS_CONFIG[deadline.status];
  const Icon = FRAMEWORK_ICONS[deadline.framework] || FileText;

  return (
    <div 
      className={`bg-[#111111] border ${statusConfig.border} rounded-xl p-4 hover:bg-[#151515] transition-all`}
      data-testid={`deadline-card-${deadline.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${statusConfig.bg}`}>
          <Icon size={20} className={statusConfig.text} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#1F1F1F] text-[#A0A0A0] rounded">
              {deadline.framework}
            </span>
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${statusConfig.bg} ${statusConfig.text}`}>
              {statusConfig.label}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-white truncate">{deadline.title}</h3>
          <p className="text-xs text-[#6B6B6B] mt-0.5">{deadline.cycle}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-lg font-bold ${deadline.daysRemaining < 30 ? 'text-red-400' : 'text-white'}`}>
            {deadline.daysRemaining}
          </div>
          <div className="text-[10px] text-[#6B6B6B] uppercase">days left</div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[#1F1F1F] flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
          <Calendar size={12} />
          <span>{new Date(deadline.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
        <span className="text-xs text-[#6B6B6B] capitalize">{deadline.type}</span>
      </div>
    </div>
  );
};

export default function UpcomingDeadlinesPage() {
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // list | calendar

  const sortedDeadlines = [...DEADLINES]
    .filter(d => filterStatus === 'all' || d.status === filterStatus)
    .sort((a, b) => a.daysRemaining - b.daysRemaining);

  const urgentCount = DEADLINES.filter(d => d.status === 'urgent').length;
  const upcomingCount = DEADLINES.filter(d => d.status === 'upcoming').length;

  return (
    <div className="space-y-6" data-testid="upcoming-deadlines-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Upcoming Deadlines</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Timeline view of all disclosure deadlines and milestones
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[#1F1F1F] text-[#6B6B6B]'}`}
          >
            List
          </button>
          <button 
            onClick={() => setViewMode('calendar')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'calendar' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[#1F1F1F] text-[#6B6B6B]'}`}
          >
            Calendar
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertTriangle size={18} className="text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-400">{urgentCount}</div>
              <div className="text-xs text-[#6B6B6B]">Urgent (&lt;30 days)</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Clock size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">{upcomingCount}</div>
              <div className="text-xs text-[#6B6B6B]">Upcoming (30-90 days)</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Calendar size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{DEADLINES.length}</div>
              <div className="text-xs text-[#6B6B6B]">Total Deadlines</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle2 size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-400">
                {Math.min(...DEADLINES.map(d => d.daysRemaining))}
              </div>
              <div className="text-xs text-[#6B6B6B]">Next Due (days)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter size={14} className="text-[#6B6B6B]" />
        <div className="flex gap-2">
          {['all', 'urgent', 'upcoming', 'planned'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                filterStatus === status 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Deadlines List */}
      <div className="space-y-3">
        {sortedDeadlines.map(deadline => (
          <DeadlineCard key={deadline.id} deadline={deadline} />
        ))}
      </div>

      {sortedDeadlines.length === 0 && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <Calendar size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No deadlines found</h3>
          <p className="text-sm text-[#6B6B6B]">All caught up! No deadlines match your filter.</p>
        </div>
      )}
    </div>
  );
}
